import React, { useEffect, useRef, useState, lazy, Suspense as ReactSuspense } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "@/lib/i18n";
import axios from "axios";
import LegalFooter from "@/components/LegalFooter";
import { toast } from "sonner";
import {
  Check,
  ExternalLink,
  Loader2,
  Trophy,
  PartyPopper,
  Sparkles,
  ArrowRight,
  Copy,
  Calendar,
  Link2,
  FileText,
  ShieldAlert,
  Volume2,
  VolumeX,
  FlaskConical,
  Download,
  Frown,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import SpinWheel from "@/components/SpinWheel";
import SpinWheelPremium from "@/components/SpinWheelPremium";
import ScratchCard from "@/components/ScratchCard";
const PenaltyShootout3D = lazy(() =>
  import("@/components/penalty3d/PenaltyShootout3D"),
);
import CardGame from "@/components/CardGame";
import SlotMachine from "@/components/SlotMachine";
import LotteryDraw from "@/components/LotteryDraw";
import PredictionMatch from "@/components/PredictionMatch";
import GuessPrice from "@/components/GuessPrice";
import QuizGame from "@/components/QuizGame";
import NinjaGame from "@/components/NinjaGame";
import BlowCandles from "@/components/BlowCandles";
import FlappyMascotte from "@/components/FlappyMascotte";
import MysteryBox from "@/components/mystery/MysteryBox";
import AdventCalendar from "@/components/AdventCalendar";
import InAppBrowserBlocker, {
  isInAppBrowser,
} from "@/components/InAppBrowserBlocker";
import CtaInstructionBanner from "@/components/CtaInstructionBanner";
import { ContentFrame } from "@/components/ContentFrame";
import { DemoLangSwitch } from "@/components/DemoLangSwitch";
import { playerErrorMessage } from "@/lib/playerErrors";
import ReceiptUploadStep from "@/components/ReceiptUploadStep";
import PetitRappelDialog from "@/components/PetitRappelDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Logo, ThemeToggle } from "@/components/Navbar";
import { API } from "@/lib/api";
import { openCtaLink } from "@/lib/deeplink";
import { loadProgress, saveProgress, clearProgress } from "@/lib/playProgress";
import { resolveImageUrl } from "@/lib/images";
import { fontStyle, loadGameFonts, sizeClass, fontScale } from "@/lib/fonts";
import { buttonStyle, overlayGradient, confettiColors } from "@/lib/gameStyle";
import { playSpin, playWin, playLose, setMuted } from "@/lib/sounds";
import { duckAmbient } from "@/lib/adventMusic";
import {
  getClientFingerprint,
  isAlreadyParticipatedError,
  alreadyParticipatedMessage,
  extractErrorMessage,
} from "@/lib/fingerprint";

// Pick the background matching the visitor's screen (shared with the waiting screen).
import { useResponsiveBackground } from "@/lib/useResponsiveBackground";
import GameWaitingScreen from "@/components/GameWaitingScreen";

const PROGRESS_STEPS = ["cta", "receipt", "play"];

export default function PublicGame({ gameProp = null }) {
  const params = useParams();
  const slug = gameProp?.slug || params.slug;
  const { t } = useTranslation();
  const [game, setGame] = useState(gameProp);
  const [waiting, setWaiting] = useState(null);
  const [err, setErr] = useState(null);
  const [step, setStep] = useState(() => {
    // If the URL carries a mystery-box unlock code (?mb_code=XXXXXX), the
    // visitor came from the email link AFTER having already participated.
    // Skip info/CTA/receipt — go straight to the play step so MysteryBox
    // can auto-call /mystery-unlock without the participant form blocking.
    try {
      const p = new URLSearchParams(window.location.search);
      if (p.get("mb_code")) return "play";
    } catch {
      /* SSR-safe noop */
    }
    return "info";
  }); // info | cta | receipt | play | result
  const [checkingEligibility, setCheckingEligibility] = useState(false);
  const [infoError, setInfoError] = useState(null);
  const [info, setInfo] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    birthdate: "",
    children_count: null,
    city: "",
    marketing_opt_in: false,
    address_line1: "",
    address_line2: "",
    postal_code: "",
    city: "",
  });
  // Capture ref query param (share attribution for lottery games)
  // and mb_code (Mystery Box: direct unlock from email link)
  const { refId, initialMbCode } = (() => {
    try {
      const p = new URLSearchParams(window.location.search);
      return {
        refId: p.get("ref") || null,
        initialMbCode: p.get("mb_code") || null,
      };
    } catch (e) {
      return { refId: null, initialMbCode: null };
    }
  })();
  const [completed, setCompleted] = useState([]); // cta types
  const [verifying, setVerifying] = useState(null); // cta type currently verifying
  // Proof-of-purchase (receipt picture + OCR)
  const [receipt, setReceipt] = useState(null); // { url, path, ocr }
  const [showRappel, setShowRappel] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [resultIndex, setResultIndex] = useState(null);
  const [result, setResult] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [alreadyParticipated, setAlreadyParticipated] = useState(null); // string message | null
  const previousLang = useRef(null);
  // Synchronous anti-double-submit guard. `spinning` state can still be
  // `false` when two effects in the game component call spin() in the same
  // render frame (e.g. BlowCandles: "all snuffed" + "timeout" colliding).
  // A ref flips synchronously so the 2nd call short-circuits immediately.
  const spinInFlightRef = useRef(false);

  useEffect(() => {
    if (game?.fonts) loadGameFonts(game.fonts);
  }, [game]);
  useEffect(() => {
    const forced = game?.force_theme;
    if (!forced) return;
    const el = document.documentElement;
    const hadDark = el.classList.contains("dark");
    if (forced === "dark") el.classList.add("dark");
    else el.classList.remove("dark");
    return () => {
      if (hadDark) el.classList.add("dark");
      else el.classList.remove("dark");
    };
  }, [game?.force_theme]);
  const prizeFont = fontStyle(game?.fonts, "prizes");
  const prizeScale = fontScale(game?.fonts, "prizes");
  const prizeImage = result?.is_win
    ? resolveImageUrl(
        result.result_image || game?.segments?.[result.result_index]?.image_url
      )
    : null;
  const bgImage = useResponsiveBackground(game);
  const frame = game?.content_frame || null;
  const onPhoto = !!bgImage && !frame;
  const [soundMuted, setSoundMuted] = useState(
    () => localStorage.getItem("qrise_muted") === "1"
  );
  useEffect(() => {
    setMuted(soundMuted);
  }, [soundMuted]);
  const adventSoundOn =
    game?.type === "advent_calendar" && (game?.advent_sound || "music_sfx") !== "off";
  const showSoundToggle = !!game?.game_sounds || adventSoundOn;
  const toggleSound = () => {
    const next = !soundMuted;
    setSoundMuted(next);
    localStorage.setItem("qrise_muted", next ? "1" : "0");
  };
  useEffect(() => {
    if (game?.type === "advent_calendar") duckAmbient(modalOpen);
  }, [modalOpen, game?.type]);
  useEffect(() => {
    if (game?.game_sounds && spinning) playSpin();
  }, [spinning, game?.game_sounds]);
  useEffect(() => {
    if (!game?.game_sounds || !modalOpen || !result) return;
    if (result.is_win) playWin();
    else playLose();
  }, [modalOpen, result, game?.game_sounds]);

  // Advertiser test link (?test=TOKEN): every public API call carries the
  // token so the backend serves the game at any date and stores plays apart.
  const [testToken] = useState(
    () => new URLSearchParams(window.location.search).get("test") || null,
  );
  useEffect(() => {
    if (!testToken) return;
    axios.defaults.headers.common["X-QRISE-Test"] = testToken;
    return () => {
      delete axios.defaults.headers.common["X-QRISE-Test"];
    };
  }, [testToken]);

  useEffect(() => {
    if (gameProp) {
      setGame(gameProp);
      return;
    }
    axios
      .get(`${API}/public/games/${slug}`)
      .then((r) => {
        if (r.data?.waiting) setWaiting(r.data);
        else setGame(r.data);
      })
      .catch((e) => setErr(e));
  }, [slug, gameProp]);

  // Advent calendar: returning players get their info pre-filled (same device).
  const adventInfoKey = game?.type === "advent_calendar" ? `qrise_advent_info_${game.id}` : null;
  useEffect(() => {
    if (!adventInfoKey) return;
    try {
      const saved = JSON.parse(localStorage.getItem(adventInfoKey) || "null");
      if (saved && typeof saved === "object") setInfo((i) => ({ ...i, ...saved }));
    } catch {
      /* ignore corrupted storage */
    }
  }, [adventInfoKey]);

  // Unfinished participation survives the reload after an external CTA (mobile).
  const progressKey = `${slug}${testToken ? ":test" : ""}`;
  const restoredRef = useRef(false);
  const [finished, setFinished] = useState(false);
  useEffect(() => {
    if (!game || restoredRef.current) return;
    // Mystery-box email link: own flow, never restored nor saved.
    if (new URLSearchParams(window.location.search).get("mb_code")) return;
    restoredRef.current = true;
    const p = loadProgress(progressKey);
    if (!p || !PROGRESS_STEPS.includes(p.step)) return;
    setInfo((i) => ({ ...i, ...p.info }));
    setCompleted(p.completed || []);
    if (p.receipt) setReceipt(p.receipt);
    setStep(p.step);
    toast.success(i18n.getFixedT(game.language || i18n.language)("play.progress_restored"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game]);
  useEffect(() => {
    if (!game || !restoredRef.current) return;
    if (result || alreadyParticipated || finished) clearProgress(progressKey);
    else if (PROGRESS_STEPS.includes(step)) saveProgress(progressKey, { step, info, completed, receipt });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game, step, info, completed, receipt, result, alreadyParticipated, finished]);

  // Force the game's configured language on the public page.
  // Remember the user's previous language so we can restore it on unmount
  // (useful when admin previews a game then returns to dashboard).
  useEffect(() => {
    if (game && game.language) {
      if (previousLang.current === null) {
        previousLang.current = i18n.language || "en";
      }
      if (i18n.language !== game.language) {
        i18n.changeLanguage(game.language);
      }
    }
    return () => {
      if (previousLang.current && i18n.language !== previousLang.current) {
        i18n.changeLanguage(previousLang.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game?.language]);

  if (isInAppBrowser())
    return (
      <InAppBrowserBlocker
        merchantName={game?.merchant_name}
        merchantLogo={game?.merchant_logo}
      />
    );

  if (err)
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-center">
        <div>
          <div className="text-3xl font-semibold tracking-tight">404</div>
          <p className="text-muted-foreground mt-2" data-testid="public-game-error">
            {playerErrorMessage(err, t("errors.game_not_found"))}
          </p>
        </div>
      </div>
    );
  if (waiting)
    return <GameWaitingScreen merchant={waiting.merchant} upcoming={waiting.upcoming} />;
  if (!game)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );

  if (alreadyParticipated) {
    return (
      <AlreadyParticipatedScreen
        game={game}
        message={alreadyParticipated}
      />
    );
  }

  const requiredCtas = (game.ctas || []).filter((c) => c.required);
  const allCtas = game.ctas || [];
  const requiresReceipt = !!game?.requires_purchase_proof;
  const canPlay = requiredCtas.every((c) => completed.includes(c.type));

  async function submitInfo(e) {
    e.preventDefault();
    if (checkingEligibility) return;
    setCheckingEligibility(true);
    setInfoError(null);
    try {
      // Early anti-replay check — happens right after the info form so a
      // blocked player sees the "already participated" screen before being
      // asked to complete CTAs + play the game (less frustration).
      const { data } = await axios.post(
        `${API}/public/games/${slug}/check-eligibility`,
        {
          phone: info.phone,
          email: info.email,
          first_name: info.first_name,
          marketing_opt_in: !!info.marketing_opt_in,
          fingerprint: getClientFingerprint(),
        }
      );
      // Returning players (advent calendar) — CTAs already done on a
      // previous day are not asked again.
      if (adventInfoKey) {
        const { first_name, last_name, email, phone, birthdate, children_count, city, marketing_opt_in } = info;
        localStorage.setItem(
          adventInfoKey,
          JSON.stringify({ first_name, last_name, email, phone, birthdate, children_count, city, marketing_opt_in })
        );
      }
      const alreadyDone = Array.isArray(data?.ctas_completed) ? data.ctas_completed : [];
      const remaining = allCtas.filter((c) => !alreadyDone.includes(c.type));
      if (alreadyDone.length > 0) setCompleted((c) => [...new Set([...c, ...alreadyDone])]);
      if (remaining.length > 0) {
        if (PetitRappelDialog.shouldShow()) setShowRappel(true);
        setStep("cta");
      } else if (requiresReceipt) setStep("receipt");
      else setStep("play");
    } catch (err) {
      if (err?.response?.data?.detail?.code === "device_identity_mismatch") {
        setInfoError(playerErrorMessage(err));
      } else if (isAlreadyParticipatedError(err)) {
        setAlreadyParticipated(alreadyParticipatedMessage(err));
      } else {
        // Network / 4xx other than 409 → don't block the player
        if (allCtas.length > 0) {
          if (PetitRappelDialog.shouldShow()) setShowRappel(true);
          setStep("cta");
        } else if (requiresReceipt) setStep("receipt");
        else setStep("play");
      }
    } finally {
      setCheckingEligibility(false);
    }
  }

  function startVerify(cta) {
    if (completed.includes(cta.type)) return;
    // Saved before leaving: the browser may reload the page on return.
    saveProgress(progressKey, { step, info, completed: [...completed, cta.type], receipt });
    openCtaLink(cta);
    setVerifying(cta.type);
    setTimeout(() => {
      setCompleted((c) => [...new Set([...c, cta.type])]);
      setVerifying(null);
    }, 2500);
  }

  async function spin() {
    if (spinInFlightRef.current || spinning) return;
    spinInFlightRef.current = true;
    setSpinning(true);
    try {
      const { data } = await axios.post(
        `${API}/public/games/${slug}/play`,
        {
          participant: info,
          ctas_completed: completed,
          fingerprint: getClientFingerprint(),
          receipt_url: receipt?.url || null,
          receipt_ocr: receipt?.ocr || null,
        }
      );
      setResultIndex(data.result_index);
      setResult(data);
      if (
        game?.type === "scratch_card" ||
        game?.type === "card_game" ||
        game?.type === "blow_candles" ||
        game?.type === "registration_gift" ||
        game?.type === "penalty_shootout"
      ) {
        // These games open the modal after user interaction (reveal callback),
        // or show an in-page confirmation (registration_gift).
        setSpinning(false);
      } else {
        setTimeout(() => {
          setModalOpen(true);
        }, 5200);
      }
    } catch (e) {
      setSpinning(false);
      spinInFlightRef.current = false;
      if (isAlreadyParticipatedError(e)) {
        setAlreadyParticipated(alreadyParticipatedMessage(e));
      } else {
        toast.error(extractErrorMessage(e, "Error"));
      }
    }
  }

  function reset() {
    setModalOpen(false);
    setSpinning(false);
    spinInFlightRef.current = false;
    setResultIndex(null);
    setResult(null);
    setCompleted([]);
    setStep("info");
    setInfo({
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      marketing_opt_in: false,
      address_line1: "",
      address_line2: "",
      postal_code: "",
      city: "",
    });
  }

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Background image or ambient glassmorphism (cyan / lavender).
          `fixed` = sized to the viewport (not the whole scrollable page), so
          the uploaded 1080×1920 / 1920×1080 visuals stay sharp and framed
          exactly like in the editor preview. */}
      {bgImage ? (
        <>
          <div
            className="fixed inset-0 bg-cover bg-center pointer-events-none"
            style={{ backgroundImage: `url(${resolveImageUrl(bgImage)})` }}
            data-testid="game-bg-image"
          />
          {/* Subtle depth overlay: dark gradient from top + bottom for legibility
              of the hero title / footer, while letting the image colors breathe
              in the middle where the cards live. */}
          <div
            className="fixed inset-0 pointer-events-none"
            style={{ background: overlayGradient(game) }}
          />
        </>
      ) : (
        <div
          className="fixed inset-0 pointer-events-none qrise-ambient-aqua"
          data-testid="game-bg-ambient"
        />
      )}

      {game.test_mode && (
        <div
          className="relative z-20 bg-amber-400 text-amber-950 px-4 py-2 text-center text-xs sm:text-sm font-bold tracking-wide shadow-[0_6px_20px_-8px_rgba(0,0,0,0.5)]"
          data-testid="test-mode-banner"
        >
          <FlaskConical className="inline h-4 w-4 mr-1.5 -mt-0.5" />
          {t("play.test_mode_banner")}
        </div>
      )}

      <header className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Logo />
        <div className="flex items-center gap-1.5">
          <DemoLangSwitch slug={game.slug} />
          {showSoundToggle && (
            <button
              type="button"
              onClick={toggleSound}
              className="h-9 w-9 inline-flex items-center justify-center rounded-full border border-border/60 bg-background/60 backdrop-blur text-muted-foreground hover:text-foreground transition-colors"
              aria-label="toggle sound"
              data-testid="sound-toggle-btn"
            >
              {soundMuted ? (
                <VolumeX className="h-4 w-4" />
              ) : (
                <Volume2 className="h-4 w-4" />
              )}
            </button>
          )}
          {!game.force_theme && <ThemeToggle />}
        </div>
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 pt-2 pb-16">
        <ContentFrame frame={frame}>
        {game.merchant_logo && (
          <div
            className="flex justify-center pt-6 pb-4 fade-up"
            data-testid="public-merchant-logo-wrap"
          >
            <div
              className="inline-flex items-center justify-center h-24 w-24 rounded-full bg-white border border-white/80 overflow-hidden"
              style={{
                boxShadow:
                  "0 18px 40px -12px rgba(10,12,24,0.35), 0 0 0 4px rgba(255,255,255,0.2)",
              }}
              data-testid="public-merchant-logo"
              title={game.merchant_name || ""}
            >
              <img
                src={game.merchant_logo}
                alt={game.merchant_name || "logo"}
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        )}
        <div className="text-center max-w-2xl mx-auto mb-10 fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-border bg-card/60 backdrop-blur text-xs font-medium text-muted-foreground mb-5">
            <Sparkles className="h-3 w-3" />
            {t("play.page_badge")}
          </div>
          <h1
            className={`${sizeClass(game.fonts, "title")} font-semibold tracking-tight`}
            style={{
              ...(game.title_color ? { color: game.title_color } : {}),
              ...fontStyle(game.fonts, "title"),
            }}
            data-testid="public-game-title"
          >
            {game.title}
          </h1>
          {game.merchant_name && (
            <div
              className={`mt-3 inline-flex items-center gap-1.5 rounded-full ${frame ? "bg-neutral-900" : "bg-black/45"} px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-white/95 backdrop-blur-md shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]`}
              data-testid="public-merchant-name"
            >
              {game.merchant_name}
            </div>
          )}
          {game.description && (
            <p
              className={`mt-4 ${sizeClass(game.fonts, "description")} text-muted-foreground leading-relaxed ${
                onPhoto
                  ? "inline-block rounded-2xl bg-black/50 px-5 py-3 backdrop-blur-md border border-white/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]"
                  : ""
              }`}
              style={{
                ...(game.description_color
                  ? { color: game.description_color }
                  : onPhoto
                    ? { color: "rgba(255,255,255,0.92)" }
                    : {}),
                ...fontStyle(game.fonts, "description"),
              }}
              data-testid="public-game-description"
            >
              {game.description}
            </p>
          )}

          {game.grand_draw_enabled && (
            <div
              className="mt-5 mx-auto max-w-lg flex items-center gap-3 rounded-2xl px-4 py-3 text-left backdrop-blur-md"
              style={{
                background:
                  "linear-gradient(120deg, rgba(212,175,55,0.16), rgba(245,228,191,0.10))",
                border: "1px solid rgba(212,175,55,0.45)",
                boxShadow: "0 10px 30px -12px rgba(212,175,55,0.35)",
              }}
              data-testid="grand-draw-banner"
            >
              <div
                className="h-9 w-9 shrink-0 rounded-full flex items-center justify-center"
                style={{
                  background:
                    "linear-gradient(180deg,#f5e4bf,#d4af37,#a98027)",
                }}
              >
                <Trophy className="h-4.5 w-4.5" style={{ color: "#1a1410", height: 18, width: 18 }} />
              </div>
              <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: "#d4af37" }}>
                  {t("play.grand_draw_kicker")}
                </div>
                <div className="text-xs sm:text-[13px] font-medium leading-snug">
                  {game.grand_draw_eligibility === "losers"
                    ? t("play.grand_draw_banner_losers", {
                        count: game.grand_draw_winners_count || 1,
                      })
                    : t("play.grand_draw_banner_all", {
                        count: game.grand_draw_winners_count || 1,
                      })}
                </div>
              </div>
            </div>
          )}

          {(step === "info" || step === "cta") && allCtas.length > 0 && (
            <CtaInstructionBanner
              ctas={allCtas}
              merchantName={game.merchant_name}
              gameType={game.type}
              solid={!!frame}
            />
          )}
        </div>

        {step === "info" && (
          <form
            onSubmit={submitInfo}
            className="qrise-card p-7 max-w-md mx-auto fade-up"
            data-testid="info-form"
          >
            <div className="text-sm font-semibold mb-5">
              {t("play.your_info")}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium">
                  {t("play.first_name")}
                </Label>
                <Input
                  required
                  name="first_name"
                  autoComplete="given-name"
                  value={info.first_name}
                  onChange={(e) =>
                    setInfo({ ...info, first_name: e.target.value })
                  }
                  className="mt-1.5 h-10 rounded-xl"
                  data-testid="player-first-name-input"
                />
              </div>
              <div>
                <Label className="text-xs font-medium">
                  {t("play.last_name")}
                </Label>
                <Input
                  required
                  name="last_name"
                  autoComplete="family-name"
                  value={info.last_name}
                  onChange={(e) =>
                    setInfo({ ...info, last_name: e.target.value })
                  }
                  className="mt-1.5 h-10 rounded-xl"
                  data-testid="player-last-name-input"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs font-medium">{t("play.email")}</Label>
                <Input
                  required
                  type="email"
                  name="email"
                  autoComplete="email"
                  value={info.email}
                  onChange={(e) => setInfo({ ...info, email: e.target.value })}
                  className="mt-1.5 h-10 rounded-xl"
                  data-testid="player-email-input"
                />
              </div>
              {game.collect_phone !== "hidden" && (
                <div className="col-span-2">
                  <Label className="text-xs font-medium">
                    {t("play.phone")}
                    {game.collect_phone === "optional"
                      ? ` (${t("play.optional_suffix", "facultatif")})`
                      : ""}
                  </Label>
                  <Input
                    required={game.collect_phone !== "optional"}
                    type="tel"
                    name="phone"
                    autoComplete="tel"
                    value={info.phone}
                    onChange={(e) => setInfo({ ...info, phone: e.target.value })}
                    className="mt-1.5 h-10 rounded-xl"
                    data-testid="player-phone-input"
                  />
                </div>
              )}
              {game.collect_birthdate && (
                <div className="col-span-2">
                  <Label className="text-xs font-medium">
                    {t("play.birthdate", "Date de naissance")}
                  </Label>
                  <Input
                    required
                    type="date"
                    value={info.birthdate}
                    onChange={(e) =>
                      setInfo({ ...info, birthdate: e.target.value })
                    }
                    className="mt-1.5 h-10 rounded-xl"
                    data-testid="player-birthdate-input"
                  />
                </div>
              )}
              {game.collect_city && game.collect_city !== "hidden" && (
                <div className="col-span-2">
                  <Label className="text-xs font-medium">
                    {t("play.city", "Ville")}
                    {game.collect_city === "optional"
                      ? ` (${t("play.optional_suffix", "facultatif")})`
                      : ""}
                  </Label>
                  <Input
                    required={game.collect_city === "required"}
                    name="city"
                    autoComplete="address-level2"
                    maxLength={120}
                    value={info.city || ""}
                    onChange={(e) => setInfo({ ...info, city: e.target.value })}
                    className="mt-1.5 h-10 rounded-xl"
                    data-testid="player-city-input"
                  />
                </div>
              )}
              {game.collect_children && game.collect_children !== "hidden" && (
                <div className="col-span-2">
                  <Label className="text-xs font-medium">
                    {t("play.children_count", "Nombre d'enfants")}
                    {game.collect_children === "optional"
                      ? ` (${t("play.optional_suffix", "facultatif")})`
                      : ""}
                  </Label>
                  <Input
                    required={game.collect_children === "required"}
                    type="number"
                    inputMode="numeric"
                    min={0}
                    max={20}
                    step={1}
                    name="children_count"
                    placeholder="0"
                    value={info.children_count ?? ""}
                    onChange={(e) =>
                      setInfo({
                        ...info,
                        children_count: e.target.value === "" ? null : Math.max(0, Math.min(20, parseInt(e.target.value, 10) || 0)),
                      })
                    }
                    className="mt-1.5 h-10 rounded-xl"
                    data-testid="player-children-input"
                  />
                </div>
              )}
              {game.requires_shipping_address && (
                <div
                  className="col-span-2 mt-2 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3"
                  data-testid="shipping-address-block"
                >
                  <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-400 mb-2">
                    {t("play.shipping_section_title")}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                      <Label className="text-xs font-medium">
                        {t("play.address_line1")}
                      </Label>
                      <Input
                        required
                        value={info.address_line1}
                        onChange={(e) =>
                          setInfo({ ...info, address_line1: e.target.value })
                        }
                        className="mt-1.5 h-10 rounded-xl"
                        data-testid="player-address-line1-input"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs font-medium">
                        {t("play.address_line2")}
                      </Label>
                      <Input
                        value={info.address_line2}
                        onChange={(e) =>
                          setInfo({ ...info, address_line2: e.target.value })
                        }
                        className="mt-1.5 h-10 rounded-xl"
                        data-testid="player-address-line2-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-medium">
                        {t("play.postal_code")}
                      </Label>
                      <Input
                        required
                        value={info.postal_code}
                        onChange={(e) =>
                          setInfo({ ...info, postal_code: e.target.value })
                        }
                        className="mt-1.5 h-10 rounded-xl"
                        data-testid="player-postal-code-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-medium">
                        {t("play.city")}
                      </Label>
                      <Input
                        required
                        value={info.city}
                        onChange={(e) =>
                          setInfo({ ...info, city: e.target.value })
                        }
                        className="mt-1.5 h-10 rounded-xl"
                        data-testid="player-city-input"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="mt-5 rounded-xl border border-border/60 p-3">
              <label
                className="flex items-start gap-2.5 cursor-pointer select-none"
                data-testid="gdpr-consent-label"
              >
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-border cursor-pointer accent-violet-600"
                  checked={info.marketing_opt_in}
                  onChange={(e) =>
                    setInfo({ ...info, marketing_opt_in: e.target.checked })
                  }
                  data-testid="gdpr-consent-checkbox"
                />
                <span className="text-[12.5px] leading-snug text-foreground/80">
                  {t("play.gdpr_consent", {
                    merchant: game?.merchant_name || "",
                  })}
                </span>
              </label>
              <p className="mt-2 text-[11px] leading-snug text-muted-foreground">
                {t("play.gdpr_hint")}
              </p>
            </div>
            {infoError && (
              <div
                className="mt-5 flex items-start gap-3 rounded-xl border border-red-300/60 bg-red-50 dark:bg-red-500/10 px-4 py-3 text-left"
                role="alert"
                data-testid="info-form-error"
              >
                <ShieldAlert className="h-5 w-5 shrink-0 text-red-600 dark:text-red-400 mt-0.5" />
                <p className="text-sm leading-snug text-red-700 dark:text-red-300">{infoError}</p>
              </div>
            )}
            <Button
              type="submit"
              disabled={checkingEligibility}
              className={`mt-6 w-full h-11 rounded-xl bg-foreground text-background hover:bg-foreground/90 disabled:opacity-60 ${sizeClass(game.fonts, "buttons")}`}
              style={buttonStyle(game)}
              data-testid="player-continue-btn"
            >
              {checkingEligibility ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : null}
              {game.button_continue_label || t("play.continue")}
              {!checkingEligibility && (
                <ArrowRight className="ml-1.5 h-4 w-4" />
              )}
            </Button>
          </form>
        )}

        {step === "cta" && (
          <div className="qrise-card p-7 max-w-md mx-auto fade-up">
            <div className="text-sm font-semibold mb-5">
              {t("play.complete_actions")}
            </div>
            <div className="space-y-3">
              {allCtas.map((cta, i) => {
                const done = completed.includes(cta.type);
                const isVer = verifying === cta.type;
                return (
                  <div
                    key={i}
                    className={`rounded-xl border p-4 flex items-center justify-between gap-3 transition-all ${
                      done
                        ? "border-emerald-500/40 bg-emerald-500/5"
                        : isVer
                          ? "border-primary/40 bg-primary/5"
                          : "border-border hover:border-foreground/20"
                    }`}
                    data-testid={`cta-${cta.type}`}
                  >
                    <div className="min-w-0">
                      <div
                        className={`font-medium ${sizeClass(game.fonts, "cta")} line-clamp-2`}
                        style={fontStyle(game.fonts, "cta")}
                      >
                        {cta.label}
                      </div>
                      <div
                        className="text-xs text-muted-foreground mt-1 leading-snug"
                        data-testid={`cta-instruction-${cta.type}`}
                      >
                        {cta.instruction?.trim() ||
                          t(`play.cta_inst_${cta.type}`, {
                            defaultValue: cta.label,
                          })}
                      </div>
                      <div className="text-[10px] text-muted-foreground/70 uppercase tracking-wider font-semibold mt-1.5">
                        {cta.required
                          ? t("play.required_badge")
                          : t("play.optional_badge")}
                      </div>
                    </div>
                    {done ? (
                      <div className="h-8 px-3 rounded-lg bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1.5 text-xs font-semibold">
                        <Check className="h-3.5 w-3.5" /> {t("play.verified")}
                      </div>
                    ) : isVer ? (
                      <div className="h-8 px-3 rounded-lg bg-primary/10 inline-flex items-center gap-1.5 text-xs font-semibold">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        {t("play.verifying")}
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 rounded-lg text-xs"
                        style={fontStyle(game.fonts, "cta")}
                        onClick={() => startVerify(cta)}
                        data-testid={`cta-action-${cta.type}`}
                      >
                        <ExternalLink className="h-3 w-3 mr-1.5" />
                        {t("play.open_link")}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
            <Button
              className={`mt-6 w-full h-11 rounded-xl bg-foreground text-background hover:bg-foreground/90 disabled:opacity-50 ${sizeClass(game.fonts, "buttons")}`}
              disabled={!canPlay}
              style={buttonStyle(game)}
              onClick={() => setStep(requiresReceipt ? "receipt" : "play")}
              data-testid="player-proceed-btn"
            >
              {game.type === "registration_gift"
                ? t("play.registration_gift_finish")
                : game.button_ready_label || t("play.ready")}
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        )}

        {step === "receipt" && (
          <ReceiptUploadStep
            merchantName={game.merchant_name}
            onComplete={(r) => {
              setReceipt(r);
              setStep("play");
            }}
            onBack={() =>
              setStep(allCtas.length > 0 ? "cta" : "info")
            }
          />
        )}

        {step === "play" && (
          <div className="fade-up">
            {game.type === "registration_gift" ? (
              <RegistrationGiftStep
                info={info}
                spinning={spinning}
                result={result}
                onStart={spin}
              />
            ) : game.type === "scratch_card" ? (
              <ScratchCard
                prizeLabel={result?.result_label || ""}
                isWin={!!result?.is_win}
                spinning={spinning}
                resultIndex={resultIndex}
                onStart={spin}
                onRevealed={() => setModalOpen(true)}
                themeColor={game.theme_color}
                scratchColor={game.scratch_color || "#1d2230"}
                merchantLogo={game.merchant_logo}
                brandName={game.merchant_name}
                labelFontFamily={prizeFont.fontFamily}
                labelFontWeight={prizeFont.fontWeight}
              />
            ) : game.type === "penalty_shootout" ? (
              <ReactSuspense
                fallback={
                  <div
                    className="qrise-card mx-auto flex aspect-[9/16] max-w-md items-center justify-center"
                    data-testid="penalty-3d-loading"
                  >
                    <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
                  </div>
                }
              >
                <PenaltyShootout3D
                  prizeLabel={result?.result_label || ""}
                  isWin={!!result?.is_win}
                  spinning={spinning}
                  resultIndex={resultIndex}
                  onStart={spin}
                  onRevealed={() => setModalOpen(true)}
                  themeColor={game.theme_color}
                  merchantName={game.merchant_name}
                />
              </ReactSuspense>
            ) : game.type === "card_game" ? (
              <CardGame
                segments={game.segments}
                winLabel={result?.result_label || ""}
                isWin={!!result?.is_win}
                spinning={spinning}
                resultIndex={resultIndex}
                onStart={spin}
                onRevealed={() => setModalOpen(true)}
                themeColor={game.theme_color}
                cardBackColor={game.card_back_color || "#1d2230"}
                merchantLogo={game.merchant_logo}
                labelFontFamily={prizeFont.fontFamily}
                labelFontWeight={prizeFont.fontWeight}
              />
            ) : game.type === "slot_machine" ? (
              <SlotMachine
                segments={game.segments}
                winLabel={result?.result_label || ""}
                isWin={!!result?.is_win}
                spinning={spinning}
                resultIndex={resultIndex}
                onStart={spin}
                onRevealed={() => setModalOpen(true)}
                themeColor={game.theme_color}
                labelFontFamily={prizeFont.fontFamily}
                labelFontWeight={prizeFont.fontWeight}
                labelFontScale={prizeScale}
              />
            ) : game.type === "lottery_draw" ? (
              <LotteryDraw
                game={game}
                participant={info}
                ctasCompleted={completed}
                refId={refId}
                receipt={receipt}
                onAlreadyParticipated={(msg) => setAlreadyParticipated(msg)}
                onSubmitted={() => setFinished(true)}
              />
            ) : game.type === "prediction_match" ? (
              <PredictionMatch
                game={game}
                participant={info}
                ctasCompleted={completed}
                receipt={receipt}
                onAlreadyParticipated={(msg) => setAlreadyParticipated(msg)}
                onSubmitted={() => setFinished(true)}
              />
            ) : game.type === "guess_price" ? (
              <GuessPrice
                game={game}
                participant={info}
                ctasCompleted={completed}
                receipt={receipt}
                onAlreadyParticipated={(msg) => setAlreadyParticipated(msg)}
                onSubmitted={() => setFinished(true)}
              />
            ) : game.type === "quiz" ? (
              <QuizGame
                game={game}
                participant={info}
                ctasCompleted={completed}
                receipt={receipt}
                refId={refId}
                onAlreadyParticipated={(msg) => setAlreadyParticipated(msg)}
                onSubmitted={() => setFinished(true)}
                onResult={(r) => {
                  setResult(r);
                  setModalOpen(true);
                }}
              />
            ) : game.type === "ninja" ? (
              <NinjaGame
                game={game}
                participant={info}
                ctasCompleted={completed}
                receipt={receipt}
                onAlreadyParticipated={(msg) => setAlreadyParticipated(msg)}
                onSubmitted={() => setFinished(true)}
              />
            ) : game.type === "flappy" ? (
              <FlappyMascotte
                game={game}
                participant={info}
                ctasCompleted={completed}
                receipt={receipt}
                onAlreadyParticipated={(msg) => setAlreadyParticipated(msg)}
                onSubmitted={() => setFinished(true)}
                onResult={(r) => {
                  setResult(r);
                  setModalOpen(true);
                }}
              />
            ) : game.type === "advent_calendar" ? (
              <AdventCalendar
                game={game}
                participant={info}
                ctasCompleted={completed}
                receipt={receipt}
                muted={soundMuted}
                onToggleMute={toggleSound}
                onAlreadyParticipated={(msg) => setAlreadyParticipated(msg)}
                onResult={(r) => {
                  setResult(r);
                  setModalOpen(true);
                }}
              />
            ) : game.type === "mystery_box" ? (
              <MysteryBox
                game={game}
                participant={info}
                ctasCompleted={completed}
                receipt={receipt}
                initialCode={initialMbCode}
                onAlreadyParticipated={(msg) => setAlreadyParticipated(msg)}
                onSubmitted={() => setFinished(true)}
                onResult={(r) => {
                  setResult(r);
                  setModalOpen(true);
                }}
              />
            ) : game.type === "blow_candles" ? (
              <BlowCandles
                segments={game.segments}
                winLabel={result?.result_label || ""}
                isWin={!!result?.is_win}
                spinning={spinning}
                resultIndex={resultIndex}
                onStart={spin}
                onRevealed={() => setModalOpen(true)}
                candlesCount={game.candles_count || 5}
                difficulty={game.candles_difficulty || "normal"}
                cakeStyle={game.cake_style || "classic"}
                maxDuration={game.candles_max_duration || 20}
                themeColor={game.theme_color}
                merchantLogo={game.merchant_logo}
              />
            ) : (
              game.wheel_design === "premium" ? (
              <SpinWheelPremium
                segments={game.segments}
                spinning={spinning}
                resultIndex={resultIndex}
                onSpin={spin}
                rimColor={game.wheel_rim_color}
                pointerColor={game.button_bg_color || "#D62828"}
                curvedText={game.wheel_curved_text}
                buttonLabel={game.wheel_button_label || t("play.wheel_play_button")}
                buttonStyle={buttonStyle(game)}
                labelFontFamily={prizeFont.fontFamily}
                labelFontWeight={prizeFont.fontWeight}
                labelFontScale={prizeScale}
                hubLogo={game.wheel_hub_logo && game.merchant_logo ? resolveImageUrl(game.merchant_logo) : null}
                showPrizeNames={game.wheel_show_prize_names !== false}
              />
              ) : (
              <SpinWheel
                segments={game.segments}
                spinning={spinning}
                resultIndex={resultIndex}
                onSpin={spin}
                showPrizeNames={game.type !== "spin_wheel" || game.wheel_show_prize_names !== false}
                labelOrientation={game.wheel_label_orientation || "horizontal"}
                labelFontFamily={prizeFont.fontFamily}
                labelFontWeight={prizeFont.fontWeight}
                labelFontScale={prizeScale}
                hubLogo={
                  game.wheel_hub_logo && game.merchant_logo
                    ? resolveImageUrl(game.merchant_logo)
                    : null
                }
              />
              )
            )}
            <p
              className={`text-center mt-6 text-sm ${
                onPhoto ? "text-white/90 [text-shadow:0_1px_6px_rgba(0,0,0,0.7)]" : "text-muted-foreground"
              }`}
              data-testid="play-ready-hint"
            >
              {game.type === "scratch_card"
                ? spinning
                  ? t("play.scratch_revealing")
                  : t("play.scratch_ready")
                : game.type === "card_game"
                  ? spinning
                    ? t("play.card_loading")
                    : t("play.card_ready")
                  : game.type === "slot_machine"
                    ? spinning
                      ? t("play.slot_loading")
                      : t("play.slot_ready")
                    : game.type === "lottery_draw"
                      ? ""
                      : game.type === "prediction_match"
                        ? ""
                        : game.type === "guess_price"
                          ? ""
                          : game.type === "quiz"
                            ? ""
                          : game.type === "ninja"
                            ? ""
                            : game.type === "flappy"
                              ? ""
                              : game.type === "mystery_box"
                                ? ""
                                : game.type === "blow_candles"
                                  ? ""
                                  : game.type === "registration_gift"
                                    ? ""
                                    : game.type === "advent_calendar"
                                      ? ""
                                      : game.type === "penalty_shootout" || game.wheel_design === "premium"
                                        ? ""
                                        : spinning
                                          ? t("play.spinning")
                                          : t("play.ready")}
            </p>
          </div>
        )}
        </ContentFrame>

        <div className="mt-16 flex justify-center text-xs">
          <div
            className="inline-flex flex-col items-center gap-1.5 rounded-2xl bg-black/55 px-4 py-3 text-white/85 backdrop-blur-md shadow-[0_10px_30px_-10px_rgba(0,0,0,0.5)]"
            data-testid="public-game-legal-footer"
          >
            {(game.rules_content || game.rules_url) && (
              <div>
                {game.rules_content ? (
                  <button
                    type="button"
                    onClick={() => setRulesOpen(true)}
                    data-testid="game-rules-link"
                    className="inline-flex items-center gap-1 font-medium text-white hover:text-amber-200 underline underline-offset-4"
                  >
                    <FileText className="h-3 w-3" />
                    {t("play.rules_link")}
                  </button>
                ) : (
                  <a
                    href={game.rules_url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="game-rules-link"
                    className="inline-flex items-center gap-1 font-medium text-white hover:text-amber-200 underline underline-offset-4"
                  >
                    <FileText className="h-3 w-3" />
                    {t("play.rules_link")}
                  </a>
                )}
              </div>
            )}
            <LegalFooter variant="light" className="pt-0.5" />
            <div className="text-[11px] text-white/60">
              {t("play.powered")}
            </div>
          </div>
        </div>
      </main>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent
          className="max-w-md p-0 overflow-hidden border-0"
          data-testid="result-dialog"
        >
          <DialogHeader>
            <DialogTitle className="sr-only">Result</DialogTitle>
          </DialogHeader>
          {result && (
            <div className="relative">
              {result.is_win && (
                <>
                  {/* Premium gold gradient header */}
                  <div
                    className="absolute inset-x-0 top-0 h-48 pointer-events-none"
                    style={{
                      background:
                        "radial-gradient(60% 100% at 50% 0%, rgba(245,228,191,0.35) 0%, rgba(212,175,55,0.12) 40%, transparent 70%)",
                    }}
                  />
                  {/* Minimal confetti strip */}
                  <div
                    aria-hidden="true"
                    className="absolute inset-x-0 top-0 h-48 overflow-hidden pointer-events-none"
                  >
                    {[...Array(14)].map((_, i) => {
                      const colors = confettiColors(game);
                      const left = (i * 7.3) % 100;
                      const delay = (i % 7) * 0.18;
                      const dur = 2.4 + ((i * 0.3) % 1.6);
                      return (
                        <span
                          key={i}
                          className="absolute top-0 block rounded-sm"
                          style={{
                            left: `${left}%`,
                            width: i % 3 === 0 ? 8 : 5,
                            height: i % 3 === 0 ? 5 : 9,
                            background: colors[i % colors.length],
                            opacity: 0.9,
                            animation: `qrise-confetti ${dur}s ${delay}s cubic-bezier(.3,.2,.2,1) infinite`,
                            transform: `rotate(${i * 23}deg)`,
                          }}
                        />
                      );
                    })}
                  </div>
                </>
              )}

              <div className="relative px-7 pt-10 pb-7 text-center">
                {result.is_win ? (
                  <div className="relative mx-auto h-20 w-20 mb-2">
                    {/* Glow */}
                    <div
                      aria-hidden="true"
                      className="absolute inset-0 rounded-full blur-2xl opacity-70"
                      style={{
                        background:
                          "radial-gradient(circle, rgba(212,175,55,0.7) 0%, rgba(212,175,55,0) 70%)",
                      }}
                    />
                    {/* Trophy coin */}
                    <div
                      className="relative h-20 w-20 rounded-full flex items-center justify-center"
                      style={{
                        background:
                          "linear-gradient(180deg, #f5e4bf 0%, #d4af37 55%, #a98027 100%)",
                        boxShadow:
                          "0 20px 40px -12px rgba(212,175,55,0.55), inset 0 2px 0 rgba(255,255,255,0.5), inset 0 -2px 0 rgba(0,0,0,0.2)",
                      }}
                    >
                      <Trophy
                        className="h-9 w-9"
                        style={{ color: "#1a1410" }}
                      />
                    </div>
                  </div>
                ) : (
                  <div
                    className="mx-auto h-16 w-16 rounded-full flex items-center justify-center bg-muted text-muted-foreground"
                    data-testid="result-lose-icon"
                  >
                    <Frown className="h-7 w-7" />
                  </div>
                )}

                <div
                  className="mt-6 text-[11px] uppercase tracking-[0.32em] font-bold"
                  style={{
                    ...(result.is_win
                      ? {
                          background:
                            "linear-gradient(90deg, #a98027 0%, #d4af37 50%, #a98027 100%)",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          backgroundClip: "text",
                        }
                      : { color: "hsl(var(--muted-foreground))" }),
                    ...fontStyle(game.fonts, "messages"),
                  }}
                  data-testid="result-label-kicker"
                >
                  {result.is_win ? t("play.you_won") : t("play.you_lost")}
                </div>

                {prizeImage && (
                  <img
                    src={prizeImage}
                    alt={result.result_label || ""}
                    className="mx-auto mt-4 max-h-36 max-w-[70%] object-contain"
                    style={{ filter: "drop-shadow(0 14px 18px rgba(0,0,0,0.3))" }}
                    data-testid="result-prize-image"
                  />
                )}

                <div
                  className={`mt-3 ${sizeClass(game.fonts, "prizes")} font-semibold tracking-tight leading-[1.05]`}
                  style={fontStyle(game.fonts, "prizes")}
                  data-testid="result-prize-label"
                >
                  {result.result_label}
                </div>

                {result.is_win && game.win_image && (
                  <img
                    src={resolveImageUrl(game.win_image)}
                    alt="win"
                    className="mx-auto mt-4 max-h-44 rounded-2xl object-contain"
                    data-testid="result-win-image"
                  />
                )}

                {result.message && (
                  <p
                    className={`mt-4 ${sizeClass(game.fonts, "messages")} text-muted-foreground leading-relaxed`}
                    style={fontStyle(game.fonts, "messages")}
                  >
                    {result.message}
                  </p>
                )}

                {result.is_win && result.coupon_code && (
                  <div
                    className="mt-6 rounded-2xl p-[1px]"
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(212,175,55,0.6) 0%, rgba(245,228,191,0.2) 50%, rgba(212,175,55,0.6) 100%)",
                    }}
                    data-testid="result-coupon-block"
                  >
                    <div className="rounded-2xl bg-card px-4 py-4">
                      <div className="text-[9px] uppercase tracking-[0.32em] font-bold text-muted-foreground">
                        {t("play.your_code")}
                      </div>
                      <div className="mt-2.5 flex items-center justify-center gap-2">
                        <div
                          className="font-mono font-bold text-2xl tracking-[0.24em] select-all"
                          data-testid="result-coupon-code"
                        >
                          {result.coupon_code}
                        </div>
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(result.coupon_code);
                              toast.success(t("play.copied"));
                            } catch {
                              toast.error(t("play.copy_error", "Copie impossible — notez le code manuellement."));
                            }
                          }}
                          className="h-8 w-8 rounded-lg hover:bg-foreground/10 inline-flex items-center justify-center text-muted-foreground"
                          data-testid="result-coupon-copy"
                          aria-label={t("play.copy")}
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      {(result.claim_deadline || result.claim_days) && (
                        <div className="mt-2 text-[11px] text-muted-foreground inline-flex items-center gap-1.5">
                          <Calendar className="h-3 w-3" />
                          {result.claim_deadline
                            ? `${t("play.valid_until")} ${new Date(result.claim_deadline).toLocaleDateString()}`
                            : t("play.valid_for_days", {
                                days: result.claim_days,
                              })}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {result.is_win && result.redemption_url && (
                  <a
                    href={result.redemption_url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-4 inline-flex items-center justify-center gap-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-foreground hover:text-foreground/80"
                    data-testid="result-redemption-link"
                  >
                    <Link2 className="h-3.5 w-3.5" />
                    {t("play.redeem_at")}
                  </a>
                )}

                {/* Per-prize "Détails supplémentaires" — shown only on
                    a winning play, between the coupon block and the
                    standard "you'll receive an email" notice. */}
                {result.is_win && result.popup_info && (
                  <div
                    className="mt-5 rounded-2xl border border-sky-300/50 bg-sky-50/60 dark:bg-sky-500/10 p-4 text-left"
                    data-testid="result-popup-info"
                  >
                    <div className="flex items-center gap-1.5">
                      <div className="h-1.5 w-1.5 rounded-full bg-sky-500" />
                      <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-sky-700 dark:text-sky-300">
                        {t("play.popup_info_title", "Détails supplémentaires")}
                      </div>
                    </div>
                    <p className="mt-2 text-sm leading-relaxed text-foreground whitespace-pre-line">
                      {result.popup_info}
                    </p>
                  </div>
                )}

                {game.grand_draw_enabled &&
                  (game.grand_draw_eligibility !== "losers" || !result.is_win) && (
                    <div
                      className="mt-5 flex items-start gap-3 rounded-2xl px-4 py-3 text-left"
                      style={{
                        background:
                          "linear-gradient(120deg, rgba(212,175,55,0.16), rgba(245,228,191,0.10))",
                        border: "1px solid rgba(212,175,55,0.45)",
                      }}
                      data-testid="result-grand-draw-notice"
                    >
                      <div
                        className="h-8 w-8 shrink-0 rounded-full flex items-center justify-center"
                        style={{
                          background: "linear-gradient(180deg,#f5e4bf,#d4af37,#a98027)",
                        }}
                      >
                        <Trophy style={{ color: "#1a1410", height: 16, width: 16 }} />
                      </div>
                      <div>
                        <div
                          className="text-[10px] font-bold uppercase tracking-[0.18em]"
                          style={{ color: "#d4af37" }}
                        >
                          {t("play.grand_draw_kicker")}
                        </div>
                        <p className="mt-0.5 text-sm leading-snug text-foreground">
                          {result.is_win
                            ? t("play.grand_draw_result_win", {
                                count: game.grand_draw_winners_count || 1,
                              })
                            : t("play.grand_draw_result_lose", {
                                count: game.grand_draw_winners_count || 1,
                              })}
                        </p>
                      </div>
                    </div>
                  )}

                {result.is_win && (
                  <div
                    className="mt-6 space-y-3"
                    data-testid="result-win-email-notice"
                  >
                    <p className="text-sm text-foreground/90 leading-relaxed">
                      {t("play.win_email_coupon")}
                    </p>
                    <p
                      className="text-base font-bold leading-relaxed"
                      style={{ color: "#ef4444" }}
                    >
                      {t("play.win_thanks")}
                    </p>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {t("play.win_spam_warning")}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <PetitRappelDialog
        open={showRappel}
        ctas={allCtas}
        gameType={game.type}
        merchantName={game.merchant_name}
        onContinue={() => setShowRappel(false)}
      />

      <Dialog open={rulesOpen} onOpenChange={setRulesOpen}>
        <DialogContent
          className="max-w-3xl max-h-[88vh] flex flex-col"
          data-testid="rules-view-dialog"
        >
          <DialogHeader>
            <DialogTitle>{t("play.rules_link")}</DialogTitle>
          </DialogHeader>
          <div
            className={`flex-1 overflow-auto rules-prose ${
              /^\s*!\[/.test(game.rules_content || "") ? "rules-doc" : "pr-2"
            }`}
            data-testid="rules-view-content"
          >
            {game.rules_content ? (
              <ReactMarkdown>{game.rules_content}</ReactMarkdown>
            ) : (
              <p className="text-muted-foreground">—</p>
            )}
          </div>
          {game.rules_url && (
            <div className="pt-2">
              <a
                href={game.rules_url}
                target="_blank"
                rel="noreferrer"
                data-testid="rules-url-link"
                className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground underline underline-offset-4"
              >
                {/\.pdf(\?|$)/i.test(game.rules_url) ? (
                  <>
                    <Download className="h-3.5 w-3.5" />
                    {t("play.rules_download_pdf")}
                  </>
                ) : (
                  game.rules_url
                )}
              </a>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AlreadyParticipatedScreen({ game, message }) {
  const { t } = useTranslation();
  const bgImage = useResponsiveBackground(game);
  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {bgImage ? (
        <>
          <div
            className="fixed inset-0 bg-cover bg-center pointer-events-none"
            style={{
              backgroundImage: `url(${resolveImageUrl(bgImage)})`,
              filter: "blur(28px) saturate(1.1)",
              transform: "scale(1.1)",
              opacity: 0.35,
            }}
          />
          <div className="fixed inset-0 pointer-events-none bg-background/70" />
        </>
      ) : (
        <div className="fixed inset-0 pointer-events-none qrise-ambient-aqua" />
      )}
      <header className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Logo />
        <ThemeToggle />
      </header>
      <main className="relative z-10 max-w-xl mx-auto px-5 sm:px-8 pt-10 pb-20">
        <div className="qrise-card p-8 sm:p-10 text-center fade-up">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-amber-500/10 border border-amber-500/30 mb-5">
            <ShieldAlert className="h-7 w-7 text-amber-600 dark:text-amber-400" />
          </div>
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-muted-foreground mb-2">
            {t("play.already_eyebrow")}
          </div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">
            {t(game.daily_prize ? "play.already_title_today" : "play.already_title")}
          </h1>
          <p className="mt-4 text-[15px] text-muted-foreground leading-relaxed">
            {message}
          </p>
          <p className="mt-2 text-[13px] text-muted-foreground/80">
            {t(game.daily_prize ? "play.already_hint_today" : "play.already_hint")}
          </p>
          <div className="mt-8 pt-6 border-t border-border/50">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-muted-foreground">
              {t("play.already_game")}
            </div>
            <div className="mt-1 text-sm font-medium">{game.title}</div>
          </div>
        </div>
        <div className="text-center mt-8 text-xs text-muted-foreground">
          {t("play.powered")}
        </div>
      </main>
    </div>
  );
}


// "Inscription cadeau" step — guaranteed-gift flow with no game animation.
// On mount, kick off the /play call. While loading we show a spinner;
// once the result arrives we render an in-page confirmation card with the
// participant's address recap. No popup — the merchant emails the recap.
function RegistrationGiftStep({ info, spinning, result, onStart }) {
  const { t } = useTranslation();
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    if (result) return;
    startedRef.current = true;
    onStart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!result) {
    return (
      <div
        className="qrise-card p-10 max-w-md mx-auto text-center"
        data-testid="registration-gift-loading"
      >
        <div className="mx-auto h-12 w-12 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-emerald-600 dark:text-emerald-400" />
        </div>
        <p className="mt-5 text-sm text-muted-foreground leading-relaxed">
          {spinning
            ? t("play.registration_gift_finish")
            : t("play.continue")}
          …
        </p>
      </div>
    );
  }

  return (
    <div
      className="qrise-card p-7 max-w-md mx-auto text-center"
      data-testid="registration-gift-confirmation"
    >
      <div className="mx-auto h-16 w-16 rounded-full flex items-center justify-center bg-emerald-500/10 border border-emerald-500/30 mb-3">
        <Trophy className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
      </div>
      <div
        className="text-[11px] uppercase tracking-[0.32em] font-bold text-emerald-600 dark:text-emerald-400"
        data-testid="registration-gift-kicker"
      >
        {t("play.registration_gift_done_kicker")}
      </div>
      <h2 className="mt-2 text-2xl sm:text-3xl font-semibold tracking-tight leading-[1.1]">
        {t("play.registration_gift_done_title")}
      </h2>
      <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
        {t("play.registration_gift_done_body")}
      </p>

      <div
        className="mt-6 rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-4 text-left"
        data-testid="registration-gift-address-recap"
      >
        <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-emerald-700 dark:text-emerald-400 mb-2">
          {t("play.registration_gift_address_recap")}
        </div>
        <div className="text-sm font-medium leading-relaxed">
          {info.first_name} {info.last_name}
          <br />
          {info.address_line1}
          {info.address_line2 ? (
            <>
              <br />
              {info.address_line2}
            </>
          ) : null}
          <br />
          {info.postal_code} {info.city}
        </div>
      </div>

      <p className="mt-5 text-xs text-muted-foreground leading-relaxed">
        {t("play.registration_gift_email_notice")}
      </p>
    </div>
  );
}
